/* ══════════════════════════════════════════════════════════
   AUDIO NOTES SURFACE — dark notes/addendum screen
   Used by AudioCapture after the recording phase. Same dark
   editorial language as AudioRecorderSurface; just swaps the
   waveform/button cluster for an attached-clips list + textarea.
   ══════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import './AudioNotesSurface.css';
import { AudioClip } from './useAudioRecorder';

export interface AudioNotesSurfaceProps {
  clips: AudioClip[];
  initialNotes?: string;
  onSubmit: (notes: string) => void;
  onSaveDraft: (notes: string) => void;
  /** Top-left back/close affordance (caller-supplied). */
  headerLeft?: React.ReactNode;
}

const MAX_CHARS = 2000;

function formatTime(sec: number): string {
  const s = Math.floor(sec);
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}

const PlayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
    <polygon points="7,4 20,12 7,20" />
  </svg>
);
const PauseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

export const AudioNotesSurface: React.FC<AudioNotesSurfaceProps> = ({
  clips,
  initialNotes,
  onSubmit,
  onSaveDraft,
  headerLeft,
}) => {
  const [notes, setNotes] = useState(initialNotes || '');
  const [playingClipId, setPlayingClipId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const togglePlay = (clip: AudioClip) => {
    if (!audioRef.current) return;
    if (playingClipId === clip.id) {
      audioRef.current.pause();
      setPlayingClipId(null);
    } else {
      audioRef.current.src = clip.url;
      audioRef.current.play().catch(() => { /* user-gesture race; ignore */ });
      setPlayingClipId(clip.id);
    }
  };

  const totalDuration = clips.reduce((sum, c) => sum + c.duration, 0);

  return (
    <div className="ans-dark ans-fade-in">
      {headerLeft}

      <div className="ans-status">ADD A NOTE</div>

      <div className="ans-prompt">
        Anything you want the researcher to know alongside your recording?
      </div>

      <div className="ans-clips-block">
        <div className="ans-clips-label">
          Attached · {clips.length} clip{clips.length !== 1 ? 's' : ''} · {formatTime(totalDuration)}
        </div>
        {clips.map((clip, idx) => {
          const isPlaying = playingClipId === clip.id;
          return (
            <div key={clip.id} className="ans-clip">
              <button
                className="ans-clip-play"
                onClick={() => togglePlay(clip)}
                aria-label={isPlaying ? `Pause clip ${idx + 1}` : `Play clip ${idx + 1}`}
                type="button"
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </button>
              <span className="ans-clip-meta">Clip {idx + 1} · {formatTime(clip.duration)}</span>
            </div>
          );
        })}
      </div>

      <div className="ans-note-wrap">
        <textarea
          className="ans-textarea"
          placeholder="Add context, reactions, or anything else…"
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, MAX_CHARS))}
        />
        <div className="ans-charcount">{notes.length} / {MAX_CHARS}</div>
      </div>

      <div className="ans-footer">
        <button
          className="ans-btn-ember"
          onClick={() => onSubmit(notes)}
          type="button"
        >
          Submit
        </button>
        <button
          className="ans-btn-ghost"
          onClick={() => onSaveDraft(notes)}
          type="button"
        >
          Save draft
        </button>
      </div>

      <audio ref={audioRef} onEnded={() => setPlayingClipId(null)} style={{ display: 'none' }} />
    </div>
  );
};
